// Centralized DOM utilities for dialog elements
export class DialogDOMUtils {
  // Common selectors as constants
  static SELECTORS = {
    BACKDROP: 'div[class*="fixed"][class*="inset-0"]',
    FIXED_NODES: 'div[class*="fixed"]',
    CLOSE_BUTTONS: 'button[data-action*="click->dialog#dismiss"]',
    ELEMENTS_WITH_STATE: '[data-state]',
    DIALOG_CONTROLLERS: '[data-controller*="dialog"]',
    RECIPE_FORMS: 'form[data-controller*="recipe_form"], form[data-controller*="recipe-form"], form[data-controller*="forms--has-remote-form"]',
    H_FULL_ELEMENTS: '.h-full',
    FOCUSABLE_ELEMENTS: 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]'
  }

  // Get backdrop element within a dialog
  static getBackdrop(dialogEl) {
    return dialogEl?.querySelector(this.SELECTORS.BACKDROP) || null
  }

  // Get all fixed nodes within a dialog
  static getFixedNodes(dialogEl) {
    return dialogEl ? Array.from(dialogEl.querySelectorAll(this.SELECTORS.FIXED_NODES)) : []
  }

  // Get panel element (fixed node that's not the backdrop)
  static getPanel(dialogEl) {
    const fixedNodes = this.getFixedNodes(dialogEl)
    return fixedNodes.find(el => !(el.className || '').includes('inset-0')) || null
  }

  // Get close buttons within a dialog
  static getCloseButtons(dialogEl) {
    return dialogEl ? Array.from(dialogEl.querySelectorAll(this.SELECTORS.CLOSE_BUTTONS)) : []
  }

  // Get elements with data-state attribute
  static getElementsWithState(dialogEl) {
    return dialogEl ? Array.from(dialogEl.querySelectorAll(this.SELECTORS.ELEMENTS_WITH_STATE)) : []
  }

  // Get all dialog controller roots
  static getAllDialogRoots() {
    return Array.from(document.querySelectorAll(this.SELECTORS.DIALOG_CONTROLLERS))
  }

  // Check if dialog has active recipe form
  static hasActiveRecipeForm(dialogEl) {
    return dialogEl ? !!dialogEl.querySelector(this.SELECTORS.RECIPE_FORMS) : false
  }

  // Get h-full elements within a dialog
  static getHFullElements(dialogEl) {
    return dialogEl ? Array.from(dialogEl.querySelectorAll(this.SELECTORS.H_FULL_ELEMENTS)) : []
  }

  // Get focusable elements within a dialog
  static getFocusableElements(dialogEl) {
    return dialogEl ? Array.from(dialogEl.querySelectorAll(this.SELECTORS.FOCUSABLE_ELEMENTS)) : []
  }

  // Check if dialog has overlay structure (backdrop or fixed nodes)
  static hasOverlayStructure(dialogEl) {
    if (!dialogEl) return false
    const backdrop = this.getBackdrop(dialogEl)
    const fixedNodes = this.getFixedNodes(dialogEl)
    return !!(backdrop || fixedNodes.length > 0)
  }

  // Comprehensive dialog structure info
  static getDialogStructure(dialogEl) {
    if (!dialogEl) return null
    
    const backdrop = this.getBackdrop(dialogEl)
    const fixedNodes = this.getFixedNodes(dialogEl)
    const panel = this.getPanel(dialogEl)
    const closeButtons = this.getCloseButtons(dialogEl)
    
    return {
      backdrop,
      panel,
      fixedNodes,
      closeButtons,
      hasBackdrop: !!backdrop,
      hasPanel: !!panel,
      hasOverlay: !!(backdrop || fixedNodes.length > 0),
      hasActiveRecipeForm: this.hasActiveRecipeForm(dialogEl)
    }
  }
}